import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table"
import { Skeleton } from "../ui/skeleton"
import { EmptyState } from "./EmptyState"
import { cn } from "../../lib/utils"

interface DataTableProps<T> {
  data: T[]
  columns: Array<{
    key: string | number
    header: string
    cell: (row: T) => React.ReactNode
    className?: string
  }>
  loading?: boolean
  onRowClick?: (row: T) => void
  emptyMessage?: string
  dense?: boolean
  getRowClassName?: (row: T) => string
  getRowKey?: (row: T) => string
  /** When provided, rows support middle-click / Ctrl+click → open this URL
   *  in a new browser tab (in addition to the normal onRowClick on left-click). */
  getRowHref?: (row: T) => string
}

export function DataTable<T extends { id?: string; chatId?: string }>({
  data,
  columns,
  loading,
  onRowClick,
  emptyMessage = "Нет данных",
  dense = true,
  getRowClassName,
  getRowKey,
  getRowHref,
}: DataTableProps<T>) {
  const getKey = (row: T): string => {
    if (getRowKey) return getRowKey(row);
    if ('_id' in row && row.id) return row.id;
    if ('chatId' in row && row.chatId) return row.chatId;
    return String(Math.random());
  };
  if (loading) {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((col, i) => (
              <TableHead key={i} className={col.className}>{col.header}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 5 }).map((_, i) => (
            <TableRow key={i}>
              {columns.map((_, j) => (
                <TableCell key={j}>
                  <Skeleton className="h-4 w-full" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )
  }

  if (data.length === 0) {
    return <EmptyState message={emptyMessage} />
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {columns.map((col, i) => (
            <TableHead key={i} className={col.className}>
              {col.header}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((row) => {
          const href = getRowHref?.(row);
          return (
          <TableRow
            key={getKey(row)}
            onClick={(e) => {
              // Ctrl/Cmd+click → new tab (don't also run the in-app nav).
              if (href && (e.ctrlKey || e.metaKey)) {
                window.open(href, '_blank', 'noopener');
                return;
              }
              onRowClick?.(row);
            }}
            // Middle-click (wheel button) → open in a new background tab.
            onAuxClick={(e) => {
              if (href && e.button === 1) {
                e.preventDefault();
                window.open(href, '_blank', 'noopener');
              }
            }}
            className={cn(
              dense ? "h-10" : undefined,
              getRowClassName?.(row)
            )}
          >
            {columns.map((col, i) => (
              <TableCell key={i} className={col.className}>
                {col.cell(row)}
              </TableCell>
            ))}
          </TableRow>
          );
        })}
      </TableBody>
    </Table>
  )
}

